import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSprings, animated } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import useLocalStorage from "@/hooks/useLocalStorage";
import { clamp, swap } from "@catchy/app-core";
import checkStopType from "@/lib/stopTypeChecker";
import StopMarker from "@catchy/assets/svgs/stop-mono-lg.svg";
const CARD_HEIGHT = 72;
function getStopTypeColorClass(stopType) {
  switch (stopType) {
    case "train":
      return "text-green-500";
    case "cable_car":
      return "text-red-500";
    case "ferry":
      return "text-blue-500";
    case "bus":
    default:
      return "text-orange-400";
  }
}
const fn = (order, active = false, activeIndex = 0, y = 0, activeBaseIndex = 0) => index => {
  const rowIndex = order.indexOf(index);
  const baseY = Math.max(rowIndex, 0) * CARD_HEIGHT;
  if (active && index === activeIndex) {
    return {
      // Keep drag anchored to where this drag started.
      y: activeBaseIndex * CARD_HEIGHT + y,
      scale: 1.03,
      zIndex: 1,
      shadow: 5,
      immediate: key => key === "y" || key === "zIndex",
      config: {
        tension: 300,
        friction: 20,
        clamp: true
      }
    };
  }
  return {
    y: baseY,
    scale: 1,
    zIndex: 0,
    shadow: 1,
    immediate: false,
    config: {
      tension: 300,
      friction: 20,
      clamp: true
    }
  };
};
const ReorderFavourites = t0 => {
  const $ = _c(34);
  const {
    onSaveOrderChange,
    type,
    typeFavourites
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [, setFavourites] = useLocalStorage("favourites", t1);
  let t2;
  if ($[1] !== typeFavourites) {
    t2 = typeFavourites.map(_temp);
    $[1] = typeFavourites;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const defaultOrder = t2;
  const order = useRef(defaultOrder);
  const hasChangedOrder = useRef(false);
  const t3 = typeFavourites.length;
  let t4;
  if ($[3] !== defaultOrder) {
    t4 = fn(defaultOrder);
    $[3] = defaultOrder;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [springs, api] = useSprings(t3, t4);
  let t5;
  let t6;
  if ($[5] !== api || $[6] !== defaultOrder) {
    t5 = () => {
      order.current = defaultOrder;
      hasChangedOrder.current = false;
      api.start(index_0 => ({
        ...fn(defaultOrder)(index_0),
        immediate: true
      }));
    };
    t6 = [defaultOrder, api];
    $[5] = api;
    $[6] = defaultOrder;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== setFavourites || $[10] !== typeFavourites) {
    t7 = async () => {
      if (!hasChangedOrder.current) {
        return;
      }
      const reorderedTypeFavourites = order.current.map(idx => typeFavourites[idx]?.stop_id).filter(Boolean);
      await setFavourites(t8 => {
        const current = t8 === undefined ? [] : t8;
        const currentList = Array.isArray(current) ? current : [];
        const typeStopIds = new Set(typeFavourites.map(_temp2));
        const otherTypeFavourites = currentList.filter(stopId => !typeStopIds.has(stopId));
        return [...reorderedTypeFavourites, ...otherTypeFavourites];
      });
      hasChangedOrder.current = false;
    };
    $[9] = setFavourites;
    $[10] = typeFavourites;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const saveOrder = t7;
  let t8;
  let t9;
  if ($[12] !== onSaveOrderChange || $[13] !== saveOrder) {
    t8 = () => {
      onSaveOrderChange?.(saveOrder);
      return () => {
        onSaveOrderChange?.(null);
      };
    };
    t9 = [onSaveOrderChange, saveOrder];
    $[12] = onSaveOrderChange;
    $[13] = saveOrder;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== api || $[17] !== defaultOrder || $[18] !== typeFavourites.length) {
    t10 = t11 => {
      const {
        args: t12,
        active,
        movement: t13
      } = t11;
      const [originalIndex] = t12;
      const [, y] = t13;
      const activeBaseIndex = order.current.indexOf(originalIndex);
      const curRow = clamp(Math.round((activeBaseIndex * CARD_HEIGHT + y) / CARD_HEIGHT), 0, typeFavourites.length - 1);
      const newOrder = swap(order.current, activeBaseIndex, curRow);
      api.start(fn(newOrder, active, originalIndex, y, activeBaseIndex));
      if (!active) {
        order.current = newOrder;
        hasChangedOrder.current = !newOrder.every((value, index_1) => value === defaultOrder[index_1]);
      }
    };
    $[16] = api;
    $[17] = defaultOrder;
    $[18] = typeFavourites.length;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const bind = useDrag(t10);
  const t11 = typeFavourites.length * CARD_HEIGHT;
  let t12;
  if ($[20] !== t11) {
    t12 = {
      height: t11
    };
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== bind || $[23] !== springs || $[24] !== type || $[25] !== typeFavourites) {
    let t14;
    if ($[27] !== bind || $[28] !== type || $[29] !== typeFavourites) {
      t14 = (t15, i) => {
        const {
          zIndex,
          shadow,
          y: y_0,
          scale
        } = t15;
        const stop_0 = typeFavourites[i];
        const key = stop_0?.stop_id ?? i;
        const stopType = stop_0?.stop_type || checkStopType(stop_0?.stop_id) || type;
        return <animated.div {...bind(i)} key={key} style={{
          zIndex,
          boxShadow: shadow.to(_temp3),
          y: y_0,
          scale
        }} className="pointer-events-auto absolute flex w-full cursor-grab touch-none select-none flex-row items-center rounded-lg bg-white p-2 active:cursor-grabbing dark:bg-zinc-800"><div key="marker" className="grid h-11 w-11 shrink-0 place-content-center pr-0.5"><StopMarker className={`${getStopTypeColorClass(stopType)} ml-[16px] mr-[16px] mt-0.5 h-[1.125rem] w-3 flex-shrink-0`} /></div><div key="label"><span className="line-clamp-1">{stop_0?.stop_name}</span></div></animated.div>;
      };
      $[27] = bind;
      $[28] = type;
      $[29] = typeFavourites;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    t13 = springs.map(t14);
    $[22] = bind;
    $[23] = springs;
    $[24] = type;
    $[25] = typeFavourites;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = <div style={t12} className="relative">{t13}</div>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
export default ReorderFavourites;
function _temp(_, index) {
  return index;
}
function _temp2(stop) {
  return stop.stop_id;
}
function _temp3(s) {
  return `rgb(229,231,235) 0px 0px 0px 1px, rgba(0,0,0,0.1) 0px ${s * 2}px ${s * 3}px -${s * 0.6}px, rgba(0, 0, 0, 0.1) 0px ${s * 0.8}px ${s * 1.2}px -${s * 0.8}px`;
}